/**
 * Etiquetas, iconos y cuentas de días de las tareas.
 *
 * La clasificación por franjas está duplicada del servidor (`services/tareas.ts`)
 * a propósito: la lista reagrupa al completar, aplazar o filtrar sin volver a
 * pedir nada, y hacerlo con una ida y vuelta por cada clic se nota. Las dos
 * copias comparan por día natural de Madrid, que es lo que hace que una tarea de
 * las 9:00 siga siendo «de hoy» a las 18:00 en vez de aparecer en rojo.
 */
import {
  Phone, Mail, MapPin, MessageCircle, Paperclip, CheckSquare,
  UserPlus, UserCircle, Building2, Inbox, Users, UserSearch,
} from 'lucide-react';
import type { Task, TaskPriority, TaskRecurrencia, TaskRelacion, TaskTipo } from '@/lib/types';

export const ETIQUETA_PRIORIDAD: Record<TaskPriority, string> = {
  high: 'Alta',
  medium: 'Media',
  low: 'Baja',
};

export const COLOR_PRIORIDAD: Record<TaskPriority, string> = {
  high: 'text-red-500',
  medium: 'text-amber-500',
  low: 'text-green-600',
};

/** Punto de color de la fila: dice la prioridad sin gastar una palabra. */
export const PUNTO_PRIORIDAD: Record<TaskPriority, string> = {
  high: 'bg-red-500',
  medium: 'bg-amber-500',
  low: 'bg-green-500',
};

export const ETIQUETA_TIPO: Record<TaskTipo, string> = {
  llamada: 'Llamada',
  email: 'Email',
  visita: 'Visita',
  whatsapp: 'WhatsApp',
  documentacion: 'Documentación',
  otro: 'Otro',
};

export const ICONO_TIPO: Record<TaskTipo, any> = {
  llamada: Phone,
  email: Mail,
  visita: MapPin,
  whatsapp: MessageCircle,
  documentacion: Paperclip,
  otro: CheckSquare,
};

export const ETIQUETA_RECURRENCIA: Record<TaskRecurrencia, string> = {
  none: 'No se repite',
  daily: 'Cada día',
  weekly: 'Cada semana',
  monthly: 'Cada mes',
};

export const ETIQUETA_RELACION: Record<TaskRelacion, string> = {
  lead: 'Lead',
  owner: 'Propietario',
  property: 'Propiedad',
  peticion: 'Petición',
  empleado: 'Empleado',
  candidato: 'Candidato',
};

export const ICONO_RELACION: Record<TaskRelacion, any> = {
  lead: UserPlus,
  owner: UserCircle,
  property: Building2,
  peticion: Inbox,
  empleado: Users,
  candidato: UserSearch,
};

export type Franja = 'vencida' | 'hoy' | 'semana' | 'futura' | 'sin-fecha';

export const ETIQUETA_FRANJA: Record<Franja, string> = {
  vencida: 'Vencidas',
  hoy: 'Hoy',
  semana: 'Esta semana',
  futura: 'Más adelante',
  'sin-fecha': 'Sin fecha',
};

/** Orden en que se pintan los grupos: lo urgente arriba. */
export const ORDEN_FRANJAS: Franja[] = ['vencida', 'hoy', 'semana', 'futura', 'sin-fecha'];

function diaMadrid(fecha: Date): string {
  return fecha.toLocaleDateString('sv-SE', { timeZone: 'Europe/Madrid' });
}

export function clasificar(tarea: Pick<Task, 'dueDate' | 'status'>, ahora: Date = new Date()): Franja {
  if (!tarea.dueDate) return 'sin-fecha';

  const hoy = diaMadrid(ahora);
  const dia = diaMadrid(new Date(tarea.dueDate));

  if (dia < hoy) return tarea.status === 'pending' ? 'vencida' : 'futura';
  if (dia === hoy) return 'hoy';

  const limiteSemana = diaMadrid(new Date(ahora.getTime() + 7 * 86_400_000));
  return dia <= limiteSemana ? 'semana' : 'futura';
}

/** Días naturales de diferencia, contados en Madrid. Negativo = ya pasó. */
function diasHasta(iso: string, ahora: Date = new Date()): number {
  const a = new Date(`${diaMadrid(new Date(iso))}T00:00:00Z`).getTime();
  const b = new Date(`${diaMadrid(ahora)}T00:00:00Z`).getTime();
  return Math.round((a - b) / 86_400_000);
}

/**
 * El vencimiento en lenguaje de persona: «Hoy», «Mañana», «Hace 3 días».
 *
 * Una fecha suelta obliga a calcular mentalmente si eso ya pasó, y es la única
 * pregunta que se hace uno mirando esta lista.
 */
export function textoVencimiento(iso: string | null, ahora: Date = new Date()): string {
  if (!iso) return 'Sin fecha';
  const dias = diasHasta(iso, ahora);

  if (dias === 0) return `Hoy, ${hora(iso)}`;
  if (dias === 1) return `Mañana, ${hora(iso)}`;
  if (dias === -1) return 'Ayer';
  if (dias < -1) return `Hace ${Math.abs(dias)} días`;
  if (dias < 7) return `En ${dias} días`;

  return new Date(iso).toLocaleDateString('es-ES', {
    day: '2-digit', month: 'short', ...(dias > 300 ? { year: 'numeric' } : {}),
  });
}

function hora(iso: string): string {
  return new Date(iso).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Madrid' });
}

/** A dónde lleva el enlace de la ficha relacionada. */
export function rutaDeRelacion(tarea: Pick<Task, 'relatedType' | 'relatedId'>): string | null {
  if (!tarea.relatedId) return null;
  switch (tarea.relatedType) {
    case 'owner': return `/owners/${tarea.relatedId}`;
    case 'property': return `/properties/${tarea.relatedId}`;
    case 'peticion': return `/leads/peticiones/${tarea.relatedId}`;
    case 'empleado': return `/equipo/personal/${tarea.relatedId}`;
    case 'candidato': return `/equipo/candidatos/${tarea.relatedId}`;
    // Los leads no tienen página propia: se abren desde su lista, que sabe
    // desplegar el detalle del que llega en la query.
    case 'lead': return `/leads/propietarios?lead=${tarea.relatedId}`;
    default: return null;
  }
}

/**
 * ISO → valor de un `<input type="datetime-local">`, que espera hora local sin
 * zona. `toISOString()` da UTC y adelantaría o atrasaría la hora dos veces.
 */
export function aInputLocal(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

export function deInputLocal(valor: string): string | null {
  if (!valor) return null;
  const d = new Date(valor);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/**
 * Atajos de fecha del alta rápida. A las 9:00 porque una tarea sin hora que
 * vence «mañana» se entiende como a primera hora, no a medianoche.
 */
export function atajoFecha(cual: 'hoy' | 'mañana' | 'semana'): string {
  const d = new Date();
  if (cual === 'mañana') d.setDate(d.getDate() + 1);
  if (cual === 'semana') d.setDate(d.getDate() + 7);
  d.setHours(9, 0, 0, 0);
  return d.toISOString();
}
